import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { getBookTypeLabel } from "@/lib/book-types";
import { BookType, TWRMode } from "@/schemas";

interface ImportModeWarningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detectedMode: TWRMode;
  portfolioMode: TWRMode;
  portfolioType: BookType;
  onUpgradeMode: () => void;
  onContinueAnyway: () => void;
  onCancel: () => void;
}

export function ImportModeWarningModal({
  open,
  onOpenChange,
  detectedMode,
  portfolioMode,
  portfolioType,
  onUpgradeMode,
  onContinueAnyway,
  onCancel,
}: ImportModeWarningModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              Mode Mismatch Detected
            </DialogTitle>
          </div>
          <DialogDescription className="pt-4 text-base">
            The imported file contains <strong>{detectedMode}</strong>{" "}
            transactions (deposits, withdrawals, or dividends), but this{" "}
            {getBookTypeLabel(portfolioType).toLowerCase()} is currently using{" "}
            <strong>{portfolioMode}</strong> calculation mode.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg border border-muted bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Simple mode:</strong> Only tracks BUY and SELL
              transactions. Performance is calculated based on price changes
              only.
            </p>
          </div>
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Advanced mode:</strong> Tracks deposits, withdrawals, and
              dividends in addition to trades. Enables Time-Weighted Return
              (TWR) calculations for accurate performance tracking with external
              cash flows.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onContinueAnyway}>
            Continue (BUY/SELL only)
          </Button>
          <Button onClick={onUpgradeMode}>Upgrade to Advanced</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
